import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAdminContext } from '../contexts/AdminContextProvider';
import {
  PlusIcon,
  RefreshIcon,
  BuildingIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  DatabaseIcon,
} from '../components/common/Icons';
import TemplatePicker from '../components/templates/TemplatePicker';
import {
  getHealthValue,
} from '../features/tenants/utils';
import * as tenantApi from '../features/tenants/api';
import { parseTenantApiError } from '../features/tenants/api/tenantApi';
import * as containerApiModule from '../features/tenants/api/containerApi';
import { useTenantStats, useTenants, useContainerStatus, useProvisioning, useTenantForm, useTenantUpgrade } from '../features/tenants/hooks';
import { TenantStats, TenantFilters, TenantTableHeader, TenantTableRow, TenantPagination, ConfirmDialog, SchemasModal, PurgeConfirmModal, DestroyContainerModal, HealthDetailModal, TenantDetailsModal, TenantFormModal, VersionSelectModal, UpgradeConfirmModal, UpgradeProgressModal, UpgradeHistoryModal, MigrationWizardModal, BrandingPreview, OnboardingWizard } from '../features/tenants/components';
import type { ConfirmDialogState, TenantSchema, Tenant } from '../features/tenants/types';
import type { AvailableUpgrade } from '../features/tenants/api/upgradeApi';
import {
  AVAILABLE_FEATURES,
  normalizeHexColor,
} from '../features/tenants/utils';

const TenantsPage: React.FC = () => {
  const { authFetch, isLoading: authLoading, user } = useAuth();
  const { enterTenant } = useAdminContext();
  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'superadmin';

  // Stats hook
  const { stats, refresh: refreshStats } = useTenantStats(authFetch);

  // Tenants hook - manages list, pagination, filters, and CRUD
  // Note: CRUD operations (create, update, archive, restore, purge) are available
  // but handlers use direct API calls for complex error handling
  const {
    tenants,
    loading: isLoading,
    error: fetchError,
    clearError: clearFetchError,
    pagination,
    setPage,
    filters,
    setFilters,
    sort,
    setSort,
    refresh: refreshTenants,
  } = useTenants(authFetch);

  // Destructure pagination for easier access
  const { page, pageSize, totalPages, total } = pagination;

  // UI state (not managed by hook)
  const [operationError, setOperationError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedHealth, setSelectedHealth] = useState('all');

  // Combined error display (fetch errors from hook + operation errors)
  const displayError = fetchError || operationError;
  const clearError = () => {
    clearFetchError();
    setOperationError(null);
  };

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [healthModalTenant, setHealthModalTenant] = useState<Tenant | null>(null);

  // Form state hook
  const {
    formData,
    formErrors,
    isSaving,
    modalError,
    setFormData,
    setFormErrors,
    clearFieldError,
    setIsSaving,
    setModalError,
    validateAndCheck,
    reset: resetForm,
    initializeForEdit,
  } = useTenantForm();

  // Config section expansion
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['basic']));

  // Available LLM models for tenant configuration
  const [availableModels, setAvailableModels] = useState<Array<{
    id: string;
    name: string;
    status: string;
    tier: string;
  }>>([]);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(null);

  const showConfirmDialog = (
    title: string,
    message: string,
    confirmLabel: string,
    confirmStyle: 'danger' | 'warning' | 'success',
    onConfirm: () => void
  ) => {
    setConfirmDialog({ isOpen: true, title, message, confirmLabel, confirmStyle, onConfirm });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog(null);
  };

  // Purge state (not in provisioning hook - complex confirmation flow)
  const [isPurging, setIsPurging] = useState<string | null>(null);

  // Purge confirmation modal state (requires slug typing for safety)
  const [showPurgeModal, setShowPurgeModal] = useState(false);
  const [purgeTarget, setPurgeTarget] = useState<Tenant | null>(null);
  const [slugConfirmation, setSlugConfirmation] = useState('');
  const [purgeError, setPurgeError] = useState<string | null>(null);

  // View Schemas modal state
  const [showSchemasModal, setShowSchemasModal] = useState(false);
  const [schemas, setSchemas] = useState<TenantSchema[]>([]);
  const [schemasLoading, setSchemasLoading] = useState(false);

  // Container status hook - manages status fetching, caching, and actions
  const {
    statuses: containerStatuses,
    transitioning: containerTransitioning,
    actionInProgress: containerActionInProgress,
    fetchStatus: fetchContainerStatus,
    refreshAll: refreshContainerStatuses,
    performAction: performContainerAction,
    clearStatus: clearContainerStatus,
  } = useContainerStatus(authFetch);

  // Provisioning hook - manages schema and container provisioning
  const {
    provisioningStatus,
    containerProgress: containerProvisioningProgress,
    isProvisioningSchema,
    isArchivingSchema,
    isProvisioningContainer,
    startProvisioning,
    provisionSchema,
    archiveSchema: archiveSchemaAction,
    provisionContainer,
    resetProvisioning,
    checkProvisioningTenants,
    checkContainerProvisioningTenants,
  } = useProvisioning(authFetch, {
    onRefreshTenants: refreshTenants,
    onRefreshStats: refreshStats,
    onFetchContainerStatus: fetchContainerStatus,
  });

  // Container modal state
  const [showDestroyContainerModal, setShowDestroyContainerModal] = useState(false);
  const [selectedContainerTenant, setSelectedContainerTenant] = useState<Tenant | null>(null);
  const [destroySlugConfirmation, setDestroySlugConfirmation] = useState('');

  // Track if initial container status fetch has been done (BUG-024 fix)
  const [initialContainerFetchDone, setInitialContainerFetchDone] = useState(false);

  // Tenant upgrade hook
  const tenantUpgrade = useTenantUpgrade(authFetch);

  // Upgrade modal state
  const [showVersionSelect, setShowVersionSelect] = useState(false);
  const [upgradeTarget, setUpgradeTarget] = useState<Tenant | null>(null);
  const [upgradeTargetVersion, setUpgradeTargetVersion] = useState('');
  const [showUpgradeConfirm, setShowUpgradeConfirm] = useState(false);
  const [showUpgradeProgress, setShowUpgradeProgress] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);

  // Upgrade history modal state
  const [upgradeHistoryTenant, setUpgradeHistoryTenant] = useState<Tenant | null>(null);
  const [showUpgradeHistory, setShowUpgradeHistory] = useState(false);

  // Migration wizard modal state
  const [migrationTarget, setMigrationTarget] = useState<Tenant | null>(null);

  // Cached upgrade availability per tenant
  const [upgradeInfo, setUpgradeInfo] = useState<Record<string, AvailableUpgrade>>({});

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  // Schema archive wrapper with confirmation dialog
  const archiveSchema = (tenant: Tenant) => {
    showConfirmDialog(
      'Archive Schema',
      `Are you sure you want to archive the schema for "${tenant.name}"? ` +
      `The schema will be renamed and preserved, but the tenant will no longer have access to their data until restored.`,
      'Archive Schema',
      'danger',
      async () => {
        closeConfirmDialog();
        setOperationError(null);
        try {
          await archiveSchemaAction(tenant.id);
        } catch (err) {
          setOperationError(err instanceof Error ? err.message : 'Failed to archive schema');
        }
      }
    );
  };

  // Refresh schemas (for use inside modal)
  const refreshSchemas = async () => {
    setSchemasLoading(true);
    try {
      const data = await tenantApi.fetchSchemas(authFetch);
      setSchemas(data.schemas || []);
    } catch (err) {
      setOperationError(err instanceof Error ? err.message : 'Failed to fetch schemas');
    } finally {
      setSchemasLoading(false);
    }
  };

  // Fetch all tenant schemas and show modal
  const fetchSchemasFromApi = async () => {
    await refreshSchemas();
    setShowSchemasModal(true);
  };

  // Container action handler - uses hook's performContainerAction with polling
  const handleContainerAction = async (tenantId: string, action: 'start' | 'stop' | 'restart') => {
    setOperationError(null);
    setSuccessMessage(null);

    const actionVerb = action === 'stop' ? 'Stopping'
      : action === 'start' ? 'Starting'
      : 'Restarting';
    setSuccessMessage(`${actionVerb} containers...`);

    const result = await performContainerAction(tenantId, action);

    if (result.success) {
      const actionPastTense = action === 'stop' ? 'stopped'
        : action === 'start' ? 'started'
        : 'restarted';
      setSuccessMessage(`Containers ${actionPastTense} successfully`);
      setTimeout(() => setSuccessMessage(null), 3000);
      // Refresh tenant list so the Status column reflects the new state
      // (backend sets tenant status to active/suspended on start/stop/restart)
      refreshTenants();
    } else {
      setSuccessMessage(null);
      setOperationError(result.error || `Failed to ${action} containers`);
    }
  };

  // Destroy container - separate from hook's action handling
  const [isDestroyingContainer, setIsDestroyingContainer] = useState(false);
  const destroyContainer = async () => {
    if (!selectedContainerTenant) return;
    if (destroySlugConfirmation !== selectedContainerTenant.slug) {
      setOperationError('Slug confirmation does not match');
      return;
    }

    setIsDestroyingContainer(true);
    setOperationError(null);
    try {
      await containerApiModule.destroyContainers(
        authFetch,
        selectedContainerTenant.id,
        selectedContainerTenant.slug,
        true
      );
      setShowDestroyContainerModal(false);
      setSelectedContainerTenant(null);
      setDestroySlugConfirmation('');
      // Clear container status from cache
      clearContainerStatus(selectedContainerTenant.id);
      // Refresh list to show updated status
      await refreshTenants();
      refreshStats();
    } catch (err) {
      setOperationError(err instanceof Error ? err.message : 'Failed to destroy containers');
    } finally {
      setIsDestroyingContainer(false);
    }
  };

  // Apply client-side health filtering (health is computed from containerStatuses)
  const displayedTenants = selectedHealth === 'all'
    ? tenants
    : tenants.filter(tenant => getHealthValue(tenant, containerStatuses, containerTransitioning) === selectedHealth);

  // Calculate not provisioned count from tenants (for stats display)
  const notProvisionedCount = tenants.filter(t => {
    if (t.isolation_level === 'row') return false;  // Row-level always ready
    if (t.isolation_level === 'schema') return !t.schema_name;
    if (t.isolation_level === 'container') {
      const cs = containerStatuses[t.id];
      return !cs || cs.status === 'not_provisioned';
    }
    return false;
  }).length;

  // Check provisioning status for any provisioning tenants on mount
  // Split container vs non-container tenants to poll the correct endpoint
  useEffect(() => {
    const provisioningTenants = tenants.filter(tenant => tenant.status === 'provisioning');

    const nonContainerIds = provisioningTenants
      .filter(t => t.isolation_level !== 'container')
      .map(t => t.id);
    const containerIds = provisioningTenants
      .filter(t => t.isolation_level === 'container')
      .map(t => t.id);

    if (nonContainerIds.length > 0) {
      checkProvisioningTenants(nonContainerIds);
    }
    if (containerIds.length > 0) {
      checkContainerProvisioningTenants(containerIds);
    }
  }, [tenants, checkProvisioningTenants, checkContainerProvisioningTenants]);

  // Fetch container status for container-isolated tenants (BUG-024 fix)
  useEffect(() => {
    if (tenants.length === 0) return;

    const containerTenants = tenants.filter(t => t.isolation_level === 'container');
    if (containerTenants.length === 0) return;

    // On initial load, fetch ALL container tenants
    // On subsequent updates, only fetch missing ones
    const tenantsToFetch = initialContainerFetchDone
      ? containerTenants.filter(t => !containerStatuses[t.id])
      : containerTenants;

    if (tenantsToFetch.length > 0) {
      tenantsToFetch.forEach(tenant => {
        fetchContainerStatus(tenant.id);
      });

      if (!initialContainerFetchDone) {
        setInitialContainerFetchDone(true);
      }
    }
  }, [tenants, initialContainerFetchDone]);

  // Wrapper to refresh all container statuses with current tenants
  const refreshAllContainerStatuses = useCallback(() => {
    refreshContainerStatuses(tenants);
  }, [tenants, refreshContainerStatuses]);

  const fetchModels = useCallback(async () => {
    try {
      const models = await tenantApi.fetchAvailableModels(authFetch);
      // Filter to only show production and staging models (case-insensitive)
      const activeModels = models.filter((m: { status: string }) =>
        ['production', 'staging'].includes(m.status.toLowerCase())
      );
      setAvailableModels(activeModels);
    } catch (err) {
      console.error('Failed to fetch models:', err);
    }
  }, [authFetch]);

  // Fetch models on mount (tenants and stats are handled by hooks)
  useEffect(() => {
    if (authLoading) return;
    fetchModels();
  }, [authLoading, fetchModels]);

  // Keyboard shortcuts: Ctrl+R to refresh, Escape to close modals
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+R or Cmd+R to refresh (BUG-024 fix, BUG-025 fix: clear messages)
      if ((e.ctrlKey || e.metaKey) && e.key === 'r') {
        e.preventDefault();
        setOperationError(null);
        setSuccessMessage(null);
        refreshTenants();
        refreshStats();
        refreshAllContainerStatuses();
      }
      // Escape to close modals
      if (e.key === 'Escape') {
        if (showPurgeModal) cancelPurge();
        if (showCreateModal) setShowCreateModal(false);
        if (showEditModal) setShowEditModal(false);
        if (showDetailsModal) setShowDetailsModal(false);
        if (showSchemasModal) setShowSchemasModal(false);
        if (healthModalTenant) setHealthModalTenant(null);
        if (showDestroyContainerModal) {
          setShowDestroyContainerModal(false);
          setSelectedContainerTenant(null);
          setDestroySlugConfirmation('');
        }
        if (showVersionSelect) { closeUpgradeModals(); }
        if (showUpgradeConfirm) { closeUpgradeModals(); }
        if (showUpgradeProgress && tenantUpgrade.upgradeStatus && ['completed', 'failed', 'rolled_back'].includes(tenantUpgrade.upgradeStatus.status)) {
          closeUpgradeModals();
        }
        if (migrationTarget) setMigrationTarget(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showPurgeModal, showCreateModal, showEditModal, showDetailsModal, showSchemasModal, healthModalTenant, showDestroyContainerModal, showUpgradeConfirm, showUpgradeProgress, refreshTenants, refreshStats, refreshAllContainerStatuses]); // eslint-disable-line react-hooks/exhaustive-deps

  // WQ-127: handleCreateTenant replaced by OnboardingWizard which handles
  // its own API call and progress tracking internally.

  const handleUpdateTenant = async () => {
    if (!selectedTenant) return;

    if (validateAndCheck(true)) {
      const errorFields = Object.keys(formErrors).map(field => field.replace(/_/g, ' '));
      setOperationError(`Please fix the following: ${errorFields.join(', ')}`);
      return;
    }

    setIsSaving(true);
    setModalError(null);
    setFormErrors({});
    try {
      await tenantApi.updateTenant(authFetch, selectedTenant.id, {
        name: formData.name,
        admin_email: formData.admin_email,
        admin_name: formData.admin_name || undefined,
        settings: formData.settings,
        branding: formData.branding,
        limits: formData.limits,
        ai_config: formData.ai_config,
        features: formData.features.filter(f => f.trim()),
        vertical: formData.vertical || undefined,
      });
      setShowEditModal(false);
      setSelectedTenant(null);
      resetForm();
      refreshTenants();
    } catch (err) {
      const typedErr = err as Error & { status?: number; errorData?: Record<string, unknown> };
      if (typedErr.status && typedErr.errorData) {
        const parsed = parseTenantApiError(typedErr.status, typedErr.errorData);
        if (Object.keys(parsed.fieldErrors).length > 0) {
          setFormErrors(parsed.fieldErrors);
        }
        setModalError(parsed.message);
      } else {
        setModalError(err instanceof Error ? err.message : 'Failed to update tenant');
      }
    }
    setIsSaving(false);
  };

  const handleArchiveTenant = (tenant: Tenant) => {
    showConfirmDialog(
      'Archive Tenant',
      `Are you sure you want to archive "${tenant.name}"? This will disable the tenant and all its users.`,
      'Archive',
      'danger',
      async () => {
        closeConfirmDialog();
        try {
          await tenantApi.archiveTenant(authFetch, tenant.id);
          refreshTenants();
          refreshStats();
        } catch (err) {
          setOperationError('Failed to archive tenant');
        }
      }
    );
  };

  const handleRestoreTenant = async (tenant: Tenant) => {
    try {
      await tenantApi.restoreTenant(authFetch, tenant.id);
      refreshTenants();
      refreshStats();
    } catch (err) {
      setOperationError(err instanceof Error ? err.message : 'Failed to restore tenant');
    }
  };

  // Open purge modal - requires typing slug to confirm
  const handlePurgeTenant = (tenant: Tenant) => {
    setPurgeTarget(tenant);
    setSlugConfirmation('');
    setPurgeError(null);
    setShowPurgeModal(true);
  };

  // Execute the purge after slug confirmation
  const executePurge = async () => {
    if (!purgeTarget) return;

    // Validate slug matches
    if (slugConfirmation !== purgeTarget.slug) {
      setPurgeError(`Slug does not match. Please type exactly: ${purgeTarget.slug}`);
      return;
    }

    setIsPurging(purgeTarget.id);
    setPurgeError(null);

    try {
      // Pass slug for server-side validation
      await tenantApi.purgeTenant(authFetch, purgeTarget.id, slugConfirmation);
      // Refresh list and stats
      await refreshTenants();
      refreshStats();
      setShowPurgeModal(false);
      setPurgeTarget(null);
      setSlugConfirmation('');
    } catch (err) {
      console.error('Purge error:', err);
      setPurgeError(err instanceof Error ? err.message : 'Failed to permanently delete tenant. Please try again.');
    } finally {
      setIsPurging(null);
    }
  };

  // Cancel purge and close modal
  const cancelPurge = () => {
    setShowPurgeModal(false);
    setPurgeTarget(null);
    setSlugConfirmation('');
    setPurgeError(null);
  };

  const handleSuspendTenant = (tenant: Tenant) => {
    const newStatus = tenant.status === 'suspended' ? 'active' : 'suspended';
    const isReactivating = newStatus === 'active';

    showConfirmDialog(
      isReactivating ? 'Reactivate Tenant' : 'Suspend Tenant',
      isReactivating
        ? `Are you sure you want to reactivate "${tenant.name}"? Users will regain access immediately.`
        : `Are you sure you want to suspend "${tenant.name}"? All users will lose access until reactivated.`,
      isReactivating ? 'Reactivate' : 'Suspend',
      isReactivating ? 'success' : 'warning',
      async () => {
        closeConfirmDialog();
        try {
          await tenantApi.updateTenant(authFetch, tenant.id, { status: newStatus });
          refreshTenants();
          refreshStats();
        } catch (err) {
          setOperationError(err instanceof Error ? err.message : 'Failed to update tenant status');
        }
      }
    );
  };

  // Fetch upgrade availability for container tenants
  const fetchUpgradeInfo = useCallback(async () => {
    const containerTenants = tenants.filter(t => t.isolation_level === 'container' && t.status === 'active');
    for (const tenant of containerTenants) {
      try {
        const info = await tenantUpgrade.checkAvailable(tenant.id);
        if (info) {
          setUpgradeInfo(prev => ({ ...prev, [tenant.id]: info }));
        }
      } catch {
        // Silently skip - non-critical
      }
    }
  }, [tenants, tenantUpgrade]);

  // Fetch upgrade info when tenants load
  useEffect(() => {
    if (tenants.length > 0 && initialContainerFetchDone) {
      fetchUpgradeInfo();
    }
  }, [tenants.length, initialContainerFetchDone]); // eslint-disable-line react-hooks/exhaustive-deps

  // Upgrade handlers
  const handleUpgradeTenant = async (tenant: Tenant) => {
    setUpgradeTarget(tenant);
    tenantUpgrade.reset();
    const avail = await tenantUpgrade.checkAvailable(tenant.id);
    if (avail && avail.has_upgrade) {
      setShowVersionSelect(true);
    } else {
      const ver = avail?.current_version || tenant.platform_version || 'unknown';
      setOperationError(
        `No upgrades available — ${tenant.name} is on v${ver} (latest registered). ` +
        'If a newer platform version exists, register it via Settings > Upgrade Management.'
      );
    }
  };

  const handleVersionSelected = async (version: string) => {
    if (!upgradeTarget) return;
    setShowVersionSelect(false);
    setUpgradeTargetVersion(version);
    await tenantUpgrade.validate(upgradeTarget.id, version);
    setShowUpgradeConfirm(true);
  };

  const handleViewUpgradeHistory = async (tenant: Tenant) => {
    setUpgradeHistoryTenant(tenant);
    setShowUpgradeHistory(true);
    await tenantUpgrade.loadHistory(tenant.id);
  };

  const handleConfirmUpgrade = async () => {
    if (!upgradeTarget || !upgradeTargetVersion) return;
    // Start the upgrade - keep confirm modal open to show loading/error state
    await tenantUpgrade.startTenantUpgrade(upgradeTarget.id, upgradeTargetVersion);
  };

  // Transition to progress modal when upgrade status is received
  useEffect(() => {
    if (showUpgradeConfirm && tenantUpgrade.upgradeStatus && !tenantUpgrade.error) {
      setShowUpgradeConfirm(false);
      setShowUpgradeProgress(true);
    }
  }, [showUpgradeConfirm, tenantUpgrade.upgradeStatus, tenantUpgrade.error]);

  // Shared cleanup for all upgrade modals — ensures tenant list refreshes
  const closeUpgradeModals = async () => {
    setShowVersionSelect(false);
    setShowUpgradeConfirm(false);
    setShowUpgradeProgress(false);
    setUpgradeTarget(null);
    setUpgradeTargetVersion('');
    tenantUpgrade.reset();
    await refreshTenants();
    await fetchUpgradeInfo();
  };

  const handleRollback = async () => {
    if (!upgradeTarget) return;
    setRollingBack(true);
    try {
      await tenantUpgrade.rollback(upgradeTarget.id);
    } finally {
      setRollingBack(false);
    }
  };

  const openEditModal = async (tenant: Tenant) => {
    // Fetch full tenant details since list returns summary only
    try {
      const fullTenant = await tenantApi.fetchTenant(authFetch, tenant.id);
      setSelectedTenant(fullTenant);
      initializeForEdit(fullTenant);
      setShowEditModal(true);
    } catch (err) {
      setOperationError(err instanceof Error ? err.message : 'Failed to load tenant details');
    }
  };

  const openDetailsModal = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setShowDetailsModal(true);
  };

  const handleEnterTenant = useCallback((tenant: Tenant) => {
    enterTenant({
      id: tenant.id,
      name: tenant.name,
      slug: tenant.slug,
      branding: tenant.branding,
    });
  }, [enterTenant]);

  const renderFormSection = (title: string, sectionId: string, children: React.ReactNode) => {
    const isExpanded = expandedSections.has(sectionId);
    return (
      <div className="form-section" style={{ marginBottom: '1rem' }}>
        <button
          type="button"
          className="form-section-header"
          onClick={() => toggleSection(sectionId)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%',
            padding: '8px 0',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--text-primary)',
            fontSize: '14px',
            fontWeight: 600
          }}
        >
          {isExpanded ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
          {title}
        </button>
        {isExpanded && (
          <div style={{ paddingLeft: '24px', paddingTop: '8px' }}>
            {children}
          </div>
        )}
      </div>
    );
  };

  const renderTenantForm = (isEdit: boolean) => (
    <div className="tenant-form">
      {renderFormSection('Basic Information', 'basic', (
        <>
          <div className="form-group">
            <label>URL Identifier {!isEdit && '*'}</label>
            <input
              type="text"
              className={`input ${formErrors.slug ? 'input-error' : ''}`}
              placeholder="e.g. acme-corp"
              value={formData.slug}
              onChange={(e) => {
                setFormData({ ...formData, slug: e.target.value.toLowerCase() });
                if (formErrors.slug) {
                  clearFieldError('slug');
                }
              }}
              disabled={isEdit}
              style={isEdit ? { opacity: 0.6 } : undefined}
            />
            {formErrors.slug && <span className="error-text">{formErrors.slug}</span>}
            {!isEdit && !formErrors.slug && (
              <span className="hint-text" style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px', display: 'block' }}>
                Used in URLs: /tenant/<strong>{formData.slug || 'your-identifier'}</strong>
              </span>
            )}
            {!isEdit && <span className="help-text">URL-safe identifier. Cannot be changed after creation.</span>}
          </div>

          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              className={`input ${formErrors.name ? 'input-error' : ''}`}
              placeholder="Acme Corporation"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                if (formErrors.name) {
                  clearFieldError('name');
                }
              }}
            />
            {formErrors.name && <span className="error-text">{formErrors.name}</span>}
          </div>

          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label>Admin Email *</label>
              <input
                type="email"
                className={`input ${formErrors.admin_email ? 'input-error' : ''}`}
                placeholder="admin@acme.com"
                value={formData.admin_email}
                onChange={(e) => {
                  setFormData({ ...formData, admin_email: e.target.value });
                  if (formErrors.admin_email) {
                    clearFieldError('admin_email');
                  }
                }}
              />
              {formErrors.admin_email && <span className="error-text">{formErrors.admin_email}</span>}
            </div>

            <div className="form-group">
              <label>Admin Name</label>
              <input
                type="text"
                className="input"
                placeholder="John Doe"
                value={formData.admin_name}
                onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
              />
            </div>
          </div>

          {!isEdit && (
            <div className="form-group">
              <label>Isolation Level</label>
              <select
                className={`select ${formErrors.isolation_level ? 'input-error' : ''}`}
                value={formData.isolation_level}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    isolation_level: e.target.value as 'row' | 'schema' | 'container'
                  });
                  if (formErrors.isolation_level) {
                    clearFieldError('isolation_level');
                  }
                }}
              >
                <option value="row">Row-Level Security (Shared DB)</option>
                <option value="schema">Schema-Based (Separate Schema)</option>
                <option value="container">Container-Based (Full Isolation)</option>
              </select>
              {formErrors.isolation_level && (
                <span className="error-text">{formErrors.isolation_level}</span>
              )}
              <span className="help-text">Cannot be changed after creation.</span>
              {formData.isolation_level === 'container' && !formErrors.isolation_level && (
                <span className="help-text" style={{ color: 'var(--info)', marginTop: '4px' }}>
                  Container isolation provides the highest security. Resources allocated based on tier.
                </span>
              )}
            </div>
          )}

          <div className="form-group">
            <label>Industry Vertical</label>
            <input
              type="text"
              className="input"
              placeholder="e.g., healthcare, finance, retail"
              value={formData.vertical}
              onChange={(e) => setFormData({ ...formData, vertical: e.target.value })}
            />
          </div>

          {!isEdit && (
            <div className="form-group" style={{ marginTop: '16px' }}>
              <label>Schema Template</label>
              <TemplatePicker
                authFetch={authFetch}
                selectedSlug={formData.template_slug || null}
                onSelect={(slug) => setFormData({ ...formData, template_slug: slug || '' })}
              />
              <span className="help-text">
                Templates provide pre-configured navigation and features for the tenant.
              </span>
            </div>
          )}
        </>
      ))}

      {renderFormSection('Branding', 'branding', (
        <>
          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label>Primary Color</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="color"
                  value={formData.branding.primaryColor}
                  onChange={(e) => setFormData({
                    ...formData,
                    branding: { ...formData.branding, primaryColor: e.target.value }
                  })}
                  style={{ width: '40px', height: '32px', padding: 0, border: 'none' }}
                />
                <input
                  type="text"
                  className={`input ${formErrors.primaryColor ? 'input-error' : ''}`}
                  value={formData.branding.primaryColor}
                  placeholder="#7c3aed"
                  onChange={(e) => setFormData({
                    ...formData,
                    branding: { ...formData.branding, primaryColor: e.target.value }
                  })}
                  onBlur={(e) => {
                    const normalized = normalizeHexColor(e.target.value);
                    if (normalized !== e.target.value) {
                      setFormData({
                        ...formData,
                        branding: { ...formData.branding, primaryColor: normalized }
                      });
                    }
                  }}
                  style={{ flex: 1 }}
                />
              </div>
              <span className="help-text">Enter hex code with or without #</span>
              {formErrors.primaryColor && <span className="error-text">{formErrors.primaryColor}</span>}
            </div>

            <div className="form-group">
              <label>Secondary Color</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="color"
                  value={formData.branding.secondaryColor}
                  onChange={(e) => setFormData({
                    ...formData,
                    branding: { ...formData.branding, secondaryColor: e.target.value }
                  })}
                  style={{ width: '40px', height: '32px', padding: 0, border: 'none' }}
                />
                <input
                  type="text"
                  className={`input ${formErrors.secondaryColor ? 'input-error' : ''}`}
                  value={formData.branding.secondaryColor}
                  placeholder="#06b6d4"
                  onChange={(e) => setFormData({
                    ...formData,
                    branding: { ...formData.branding, secondaryColor: e.target.value }
                  })}
                  onBlur={(e) => {
                    const normalized = normalizeHexColor(e.target.value);
                    if (normalized !== e.target.value) {
                      setFormData({
                        ...formData,
                        branding: { ...formData.branding, secondaryColor: normalized }
                      });
                    }
                  }}
                  style={{ flex: 1 }}
                />
              </div>
              <span className="help-text">Enter hex code with or without #</span>
              {formErrors.secondaryColor && <span className="error-text">{formErrors.secondaryColor}</span>}
            </div>
          </div>

          <div className="form-group">
            <label>Custom App Name</label>
            <input
              type="text"
              className="input"
              placeholder="My Custom Platform"
              value={formData.branding.appName || ''}
              onChange={(e) => setFormData({
                ...formData,
                branding: { ...formData.branding, appName: e.target.value }
              })}
            />
          </div>

          <div className="form-group">
            <label>Logo URL</label>
            <input
              type="url"
              className="input"
              placeholder="https://example.com/logo.png"
              value={formData.branding.logo || ''}
              onChange={(e) => setFormData({
                ...formData,
                branding: { ...formData.branding, logo: e.target.value }
              })}
            />
          </div>

          <div className="form-group">
            <label>Favicon URL</label>
            <input
              type="url"
              className="input"
              placeholder="https://example.com/favicon.ico"
              value={formData.branding.favicon || ''}
              onChange={(e) => setFormData({
                ...formData,
                branding: { ...formData.branding, favicon: e.target.value }
              })}
            />
            <span className="help-text">16x16 or 32x32 .ico or .png recommended</span>
          </div>

          {/* Branding Preview */}
          <BrandingPreview
            primaryColor={formData.branding.primaryColor}
            secondaryColor={formData.branding.secondaryColor}
            logoUrl={formData.branding.logo || undefined}
            faviconUrl={formData.branding.favicon || undefined}
            tenantName={formData.name}
            appName={formData.branding.appName || undefined}
          />
        </>
      ))}

      {/* Navigation overrides: UI hidden, re-enable when DynamicNavRenderer consumes these fields.
         DB fields and schema preserved for future use. Removed in WQ-088. */}

      {renderFormSection('Resource Limits', 'limits', (
        <>
          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label>Max Users</label>
              <input
                type="number"
                className="input"
                min="1"
                value={formData.limits.maxUsers}
                onChange={(e) => setFormData({
                  ...formData,
                  limits: { ...formData.limits, maxUsers: parseInt(e.target.value) || 100 }
                })}
              />
            </div>

            <div className="form-group">
              <label>Max Storage (GB)</label>
              <input
                type="number"
                className="input"
                min="1"
                value={formData.limits.maxStorageGB}
                onChange={(e) => setFormData({
                  ...formData,
                  limits: { ...formData.limits, maxStorageGB: parseInt(e.target.value) || 50 }
                })}
              />
            </div>

            <div className="form-group">
              <label>API Calls/Day</label>
              <input
                type="number"
                className="input"
                min="100"
                value={formData.limits.maxApiCallsPerDay}
                onChange={(e) => setFormData({
                  ...formData,
                  limits: { ...formData.limits, maxApiCallsPerDay: parseInt(e.target.value) || 10000 }
                })}
              />
            </div>
          </div>
        </>
      ))}

      {renderFormSection('AI Configuration', 'ai', (
        <>
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={formData.ai_config.enabled}
                onChange={(e) => setFormData({
                  ...formData,
                  ai_config: { ...formData.ai_config, enabled: e.target.checked }
                })}
              />
              Enable AI Features
            </label>
          </div>

          {formData.ai_config.enabled && (
            <>
              <div className="form-group">
                <label>Daily Token Limit</label>
                <input
                  type="number"
                  className="input"
                  min="0"
                  value={formData.ai_config.dailyTokenLimit}
                  onChange={(e) => setFormData({
                    ...formData,
                    ai_config: { ...formData.ai_config, dailyTokenLimit: parseInt(e.target.value) || 100000 }
                  })}
                />
              </div>

              <div className="form-group">
                <label>Allowed Models</label>
                {availableModels.length === 0 ? (
                  <div style={{ padding: '12px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', color: 'var(--text-secondary)' }}>
                    Loading available models...
                  </div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '8px',
                    padding: '12px',
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-secondary)',
                    maxHeight: '200px',
                    overflowY: 'auto',
                  }}>
                    {availableModels.map((model) => (
                      <label
                        key={model.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 12px',
                          background: formData.ai_config.modelsAllowed.includes(model.id)
                            ? 'rgba(124, 58, 237, 0.15)'
                            : 'rgba(255,255,255,0.02)',
                          border: formData.ai_config.modelsAllowed.includes(model.id)
                            ? '1px solid rgba(124, 58, 237, 0.4)'
                            : '1px solid transparent',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.ai_config.modelsAllowed.includes(model.id)}
                          onChange={(e) => {
                            const newModels = e.target.checked
                              ? [...formData.ai_config.modelsAllowed, model.id]
                              : formData.ai_config.modelsAllowed.filter(m => m !== model.id);
                            setFormData({
                              ...formData,
                              ai_config: { ...formData.ai_config, modelsAllowed: newModels }
                            });
                          }}
                          style={{ accentColor: 'var(--primary)' }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>
                            {model.name}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                            {model.tier} • {model.status.toLowerCase()}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
                <span style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '6px', display: 'block' }}>
                  {formData.ai_config.modelsAllowed.length} model{formData.ai_config.modelsAllowed.length !== 1 ? 's' : ''} selected
                </span>
              </div>

              <div className="form-group">
                <label>Custom AI Guidelines</label>
                <textarea
                  className="input"
                  rows={3}
                  placeholder="Custom behavior instructions for AI..."
                  value={formData.ai_config.customGuidelines || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    ai_config: { ...formData.ai_config, customGuidelines: e.target.value }
                  })}
                  style={{ resize: 'vertical' }}
                />
              </div>
            </>
          )}
        </>
      ))}

      {renderFormSection('Feature Flags', 'features', (
        <>
          <p style={{
            fontSize: '13px',
            color: 'var(--text-secondary)',
            marginBottom: '12px'
          }}>
            Enable or disable specific platform features for this tenant.
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: '10px',
          }}>
            {AVAILABLE_FEATURES.map((feature) => (
              <label
                key={feature.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  padding: '12px',
                  background: formData.features.includes(feature.id)
                    ? 'rgba(16, 185, 129, 0.1)'
                    : 'rgba(255,255,255,0.02)',
                  border: formData.features.includes(feature.id)
                    ? '1px solid rgba(16, 185, 129, 0.3)'
                    : '1px solid var(--border-secondary)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                <input
                  type="checkbox"
                  checked={formData.features.includes(feature.id)}
                  onChange={(e) => {
                    const newFeatures = e.target.checked
                      ? [...formData.features, feature.id]
                      : formData.features.filter(f => f !== feature.id);
                    setFormData({ ...formData, features: newFeatures });
                  }}
                  style={{ marginTop: '2px', accentColor: 'var(--success)' }}
                />
                <div>
                  <div style={{ fontWeight: 500, fontSize: '13px', marginBottom: '2px' }}>
                    {feature.label}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                    {feature.description}
                  </div>
                </div>
              </label>
            ))}
          </div>
          <div style={{
            marginTop: '12px',
            fontSize: '12px',
            color: 'var(--text-secondary)'
          }}>
            {formData.features.length} feature{formData.features.length !== 1 ? 's' : ''} enabled
          </div>
        </>
      ))}

      {renderFormSection('Settings', 'settings', (
        <>
          <div style={{
            marginBottom: '12px',
            padding: '8px 12px',
            background: 'rgba(148, 163, 184, 0.08)',
            border: '1px solid rgba(148, 163, 184, 0.2)',
            borderRadius: '6px',
            fontSize: '12px',
            color: 'var(--text-secondary)',
          }}>
            These settings are saved for future use but not yet applied to tenant rendering.
          </div>
          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label>Timezone</label>
              <select
                className="select"
                value={formData.settings.timezone}
                onChange={(e) => setFormData({
                  ...formData,
                  settings: { ...formData.settings, timezone: e.target.value }
                })}
              >
                <option value="UTC">UTC</option>
                <option value="America/New_York">Eastern Time</option>
                <option value="America/Chicago">Central Time</option>
                <option value="America/Denver">Mountain Time</option>
                <option value="America/Los_Angeles">Pacific Time</option>
                <option value="Europe/London">London</option>
                <option value="Europe/Paris">Paris</option>
                <option value="Asia/Tokyo">Tokyo</option>
              </select>
            </div>

            <div className="form-group">
              <label>Date Format</label>
              <select
                className="select"
                value={formData.settings.dateFormat}
                onChange={(e) => setFormData({
                  ...formData,
                  settings: { ...formData.settings, dateFormat: e.target.value }
                })}
              >
                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              </select>
            </div>

            <div className="form-group">
              <label>Locale</label>
              <select
                className="select"
                value={formData.settings.locale}
                onChange={(e) => setFormData({
                  ...formData,
                  settings: { ...formData.settings, locale: e.target.value }
                })}
              >
                <option value="en-US">English (US)</option>
                <option value="en-GB">English (UK)</option>
                <option value="es-ES">Spanish</option>
                <option value="fr-FR">French</option>
                <option value="de-DE">German</option>
                <option value="ja-JP">Japanese</option>
              </select>
            </div>
          </div>
        </>
      ))}
    </div>
  );

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">Tenant Management</h1>
          <p className="page-subtitle">
            Manage platform tenants ({total} total
            {stats && `, ${stats.active_tenants} active`})
          </p>
        </div>
        <div className="page-header-right">
          <button
            className="btn btn-secondary"
            onClick={fetchSchemasFromApi}
            disabled={schemasLoading}
            title="View all tenant schemas"
          >
            <DatabaseIcon size={16} />
            {schemasLoading ? 'Loading...' : 'View Schemas'}
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              resetForm();
              setExpandedSections(new Set(['basic']));
              setShowCreateModal(true);
            }}
          >
            <PlusIcon size={16} />
            Add Tenant
          </button>
          <button
            className="btn btn-ghost"
            onClick={() => { setOperationError(null); setSuccessMessage(null); refreshTenants(); refreshStats(); refreshAllContainerStatuses(); }}
            title="Refresh tenant list (Ctrl+R)"
          >
            <RefreshIcon size={18} />
          </button>
        </div>
      </div>

      {displayError && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
          {displayError}
          <button
            className="btn btn-ghost btn-sm"
            onClick={clearError}
            style={{ marginLeft: 'auto' }}
          >
            Dismiss
          </button>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
          <CheckCircleIcon size={16} />
          {successMessage}
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setSuccessMessage(null)}
            style={{ marginLeft: 'auto' }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Stats Cards */}
      <TenantStats stats={stats} notProvisionedCount={notProvisionedCount} />

      {/* Filters */}
      <TenantFilters
        filters={filters}
        setFilters={setFilters}
        selectedHealth={selectedHealth}
        setSelectedHealth={setSelectedHealth}
      />

      {/* Tenants Table */}
      <div className="glass-card" style={{ overflowX: 'auto', maxWidth: '100%' }}>
        {isLoading ? (
          <div className="loading-state">
            <div className="spinner" />
            <p>Loading tenants...</p>
          </div>
        ) : tenants.length === 0 ? (
          <div className="empty-state">
            <BuildingIcon size={48} />
            <p>No tenants found</p>
            <button
              className="btn btn-primary"
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
            >
              <PlusIcon size={16} />
              Create First Tenant
            </button>
          </div>
        ) : (
          <>
            <table className="data-table" style={{ minWidth: '1100px' }}>
              <TenantTableHeader sortBy={sort.by} sortDir={sort.dir} onSort={setSort} />
              <tbody>
                {displayedTenants.map((tenant) => (
                  <TenantTableRow
                    key={tenant.id}
                    tenant={tenant}
                    containerStatuses={containerStatuses}
                    containerTransitioning={containerTransitioning}
                    provisioningStatus={provisioningStatus}
                    containerProvisioningProgress={containerProvisioningProgress}
                    isPurging={isPurging}
                    upgradeInfo={upgradeInfo}
                    onViewDetails={openDetailsModal}
                    onEdit={openEditModal}
                    onSuspend={handleSuspendTenant}
                    onRestore={handleRestoreTenant}
                    onPurge={handlePurgeTenant}
                    onArchive={handleArchiveTenant}
                    onHealthClick={setHealthModalTenant}
                    onRetryProvisioning={startProvisioning}
                    onRetryContainerProvisioning={provisionContainer}
                    onResetProvisioning={async (tenantId: string) => {
                      setOperationError(null);
                      try {
                        await resetProvisioning(tenantId);
                        setSuccessMessage('Provisioning status reset successfully');
                        setTimeout(() => setSuccessMessage(null), 3000);
                      } catch (err) {
                        setOperationError(
                          err instanceof Error ? err.message : 'Failed to reset provisioning status'
                        );
                      }
                    }}
                    onUpgrade={handleUpgradeTenant}
                    onViewUpgradeHistory={handleViewUpgradeHistory}
                    onContainerAction={handleContainerAction}
                    onMigrate={(tenant) => setMigrationTarget(tenant)}
                    onEnterTenant={handleEnterTenant}
                  />
                ))}
              </tbody>
            </table>

            <TenantPagination
              page={page}
              pageSize={pageSize}
              totalPages={totalPages}
              total={total}
              onPageChange={setPage}
            />
          </>
        )}
      </div>

      {/* Health Detail Modal */}
      <HealthDetailModal
        tenant={healthModalTenant}
        onClose={() => setHealthModalTenant(null)}
        containerStatuses={containerStatuses}
        containerTransitioning={containerTransitioning}
        isProvisioningSchema={isProvisioningSchema}
        onProvisionSchema={provisionSchema}
        isProvisioningContainer={isProvisioningContainer}
        containerProvisioningProgress={containerProvisioningProgress}
        onProvisionContainer={provisionContainer}
        containerActionInProgress={containerActionInProgress}
        onContainerAction={handleContainerAction}
        authFetch={authFetch}
      />

      {/* Create Tenant Modal — WQ-127: Onboarding Wizard replaces TenantFormModal for create mode */}
      <OnboardingWizard
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setModalError(null);
          setFormErrors({});
        }}
        onComplete={() => {
          setShowCreateModal(false);
          refreshTenants();
          refreshStats();
        }}
        authFetch={authFetch}
      />

      {/* Edit Tenant Modal */}
      <TenantFormModal
        mode="edit"
        isOpen={showEditModal}
        tenant={selectedTenant}
        onClose={() => { setShowEditModal(false); setModalError(null); setFormErrors({}); }}
        onSubmit={handleUpdateTenant}
        isSaving={isSaving}
        error={modalError}
        onClearError={() => setModalError(null)}
      >
        {renderTenantForm(true)}
      </TenantFormModal>

      {/* Tenant Details Modal */}
      <TenantDetailsModal
        isOpen={showDetailsModal}
        tenant={selectedTenant}
        onClose={() => { setShowDetailsModal(false); fetchUpgradeInfo(); }}
        onEdit={openEditModal}
        containerStatuses={containerStatuses}
        isArchivingSchema={isArchivingSchema}
        onArchiveSchema={archiveSchema}
        isProvisioningSchema={isProvisioningSchema}
        onProvisionSchema={provisionSchema}
        upgradeInfo={selectedTenant ? upgradeInfo[selectedTenant.id] || null : null}
        onUpgrade={handleUpgradeTenant}
        authFetch={authFetch}
        isSuperAdmin={isSuperAdmin}
        onEnterTenant={handleEnterTenant}
      />

      {/* Styled Confirmation Dialog */}
      <ConfirmDialog dialog={confirmDialog} onClose={closeConfirmDialog} />

      {/* View Schemas Modal */}
      <SchemasModal
        isOpen={showSchemasModal}
        onClose={() => setShowSchemasModal(false)}
        schemas={schemas}
        loading={schemasLoading}
        onRefresh={refreshSchemas}
      />

      {/* Purge Confirmation Modal - Requires Slug Typing for Safety */}
      <PurgeConfirmModal
        isOpen={showPurgeModal}
        tenant={purgeTarget}
        onClose={cancelPurge}
        onConfirm={executePurge}
        slugConfirmation={slugConfirmation}
        onSlugChange={setSlugConfirmation}
        isPurging={isPurging === purgeTarget?.id}
        error={purgeError}
        onClearError={() => setPurgeError(null)}
      />

      {/* Destroy Container Confirmation Modal */}
      <DestroyContainerModal
        isOpen={showDestroyContainerModal}
        tenant={selectedContainerTenant}
        onClose={() => setShowDestroyContainerModal(false)}
        onConfirm={destroyContainer}
        slugConfirmation={destroySlugConfirmation}
        onSlugChange={setDestroySlugConfirmation}
        isDestroying={isDestroyingContainer}
      />

      {/* Version Select Modal */}
      <VersionSelectModal
        isOpen={showVersionSelect}
        tenant={upgradeTarget}
        currentVersion={tenantUpgrade.available?.current_version || ''}
        versions={tenantUpgrade.available?.versions_detail || []}
        loading={tenantUpgrade.loading}
        onClose={closeUpgradeModals}
        onSelect={handleVersionSelected}
      />

      {/* Upgrade Confirm Modal */}
      <UpgradeConfirmModal
        isOpen={showUpgradeConfirm}
        tenant={upgradeTarget}
        fromVersion={tenantUpgrade.available?.current_version || ''}
        toVersion={upgradeTargetVersion}
        validation={tenantUpgrade.validation}
        loading={tenantUpgrade.loading}
        error={tenantUpgrade.error}
        onClose={closeUpgradeModals}
        onConfirm={handleConfirmUpgrade}
      />

      {/* Upgrade Progress Modal */}
      <UpgradeProgressModal
        isOpen={showUpgradeProgress}
        tenant={upgradeTarget}
        status={tenantUpgrade.upgradeStatus}
        onClose={closeUpgradeModals}
        onRollback={handleRollback}
        rolling={rollingBack}
      />

      {/* Upgrade History Modal */}
      <UpgradeHistoryModal
        isOpen={showUpgradeHistory}
        tenant={upgradeHistoryTenant}
        history={tenantUpgrade.history}
        loading={tenantUpgrade.loading}
        onClose={() => { setShowUpgradeHistory(false); setUpgradeHistoryTenant(null); }}
        onRefresh={() => { if (upgradeHistoryTenant) tenantUpgrade.loadHistory(upgradeHistoryTenant.id); }}
      />

      {/* Migration Wizard Modal */}
      <MigrationWizardModal
        isOpen={!!migrationTarget}
        tenant={migrationTarget}
        onClose={() => setMigrationTarget(null)}
        onComplete={() => { setMigrationTarget(null); refreshTenants(); refreshStats(); }}
        authFetch={authFetch}
      />

    </div>
  );
};

export default TenantsPage;
